/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: lookup_assignment_sides entity configuration
 * Generator: scripts/generate-api-clients.ts
 *
 * This file contains the API client for the lookup_assignment_sides table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the lookup_assignment_sides API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, name, code, description, created_at, updated_at]
 * Searchable fields: []
 * Filterable fields: [id]
 */

import { httpClient } from '../http-client.js';
import { type UnifiedQueryOptions } from '@config/search/global-search-config';
import type {
  LookupAssignmentSidesRow,
  LookupAssignmentSidesInsert,
  LookupAssignmentSidesUpdate
} from '../../types/generated_schemas/generated-lookup_assignment_sides-schema.js';

export interface LookupAssignmentSidesQueryOptions extends UnifiedQueryOptions {}

export interface LookupAssignmentSidesResponse {
  data: LookupAssignmentSidesRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for lookup_assignment_sides table operations
 *
 * @description Provides type-safe access to lookup_assignment_sides API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class LookupAssignmentSidesClient {
  private readonly basePath = '/lookup-assignment-sides';

  /**
   * Get all lookup_assignment_sides records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of lookup_assignment_sides records
   */
  async getAll(options?: LookupAssignmentSidesQueryOptions): Promise<LookupAssignmentSidesRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sort', options.sortBy);
    if (options?.sortOrder) params.set('order', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<LookupAssignmentSidesResponse>(url);
    return response.data;
  }

  /**
   * Get a single lookup_assignment_sides record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the lookup_assignment_sides record
   */
  async getById(id: string): Promise<LookupAssignmentSidesRow> {
    const response = await httpClient.get<{
      data: LookupAssignmentSidesRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new lookup_assignment_sides record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created lookup_assignment_sides record
   */
  async create(data: LookupAssignmentSidesInsert): Promise<LookupAssignmentSidesRow> {
    const response = await httpClient.post<{
      data: LookupAssignmentSidesRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing lookup_assignment_sides record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated lookup_assignment_sides record
   */
  async update(id: string, data: LookupAssignmentSidesUpdate): Promise<LookupAssignmentSidesRow> {
    const response = await httpClient.put<{
      data: LookupAssignmentSidesRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a lookup_assignment_sides record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple lookup_assignment_sides records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created lookup_assignment_sides records
   */
  async createMany(data: LookupAssignmentSidesInsert[]): Promise<LookupAssignmentSidesRow[]> {
    const response = await httpClient.post<{
      data: LookupAssignmentSidesRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Search lookup_assignment_sides with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    _query: string,
    _columns?: string[],
    _options?: LookupAssignmentSidesQueryOptions
  ): Promise<LookupAssignmentSidesResponse> {
    throw new Error('Search operations are not available for lookup_assignment_sides - no searchable fields configured');
  }

  /**
   * Get count of lookup_assignment_sides records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a lookup_assignment_sides record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'lookup_assignment_sides',
      publicFields: ['id', 'name', 'code', 'description', 'created_at', 'updated_at'],
      searchableFields: [],
      filterableFields: ['id'],
      sortableFields: ['created_at', 'updated_at'],
      readonlyFields: ['id', 'created_at', 'updated_at'],
      requiredFields: ['id', 'name', 'code', 'updated_at'],
    };
  }
}

// Export singleton instance for convenience
export const lookupAssignmentSidesClient = new LookupAssignmentSidesClient();
